import type { MedicalMetadata } from '@/modules/Editor/MedicalMetadata'
import type { WindowLevels } from '@/modules/Editor/imageManipulation'

/**
 * Scale back to raw pixel values, which we remove when encoding in RG channels.
 *
 * @param windowLevelRange The unscaled pixel value in the DICOM png.
 * @param medicalMetadata The medical metadata on the view, if present.
 *
 * @returns The raw value present in the original DICOM.
 */
const pixelToRaw = (
  pixelValue: number,
  numPixelChannels: number,
  medicalMetadata?: MedicalMetadata,
): number => {
  if (!medicalMetadata) {
    return pixelValue
  }

  const smallestImagePixelValue = Number(medicalMetadata.smallestImagePixelValue)
  const largestImagePixelValue = Number(medicalMetadata.largestImagePixelValue)

  const pixelRange = largestImagePixelValue - smallestImagePixelValue

  // Scale within range
  let rawPixelValue = (pixelValue / numPixelChannels) * pixelRange

  // add offset
  rawPixelValue += smallestImagePixelValue

  return rawPixelValue
}

/**
 * Scale the range using the DICOM headers for slope and intercept.
 *
 * @param rawPixelValue The raw DICOM pixel value.
 * @param medicalMetadata The medical metadata on the view, if present.
 *
 * @returns The scaled window level range.
 */
const rawToScaled = (rawPixelValue: number, medicalMetadata?: MedicalMetadata): number => {
  let rescaleIntercept = 0
  let rescaleSlope = 1

  if (medicalMetadata) {
    rescaleIntercept = Number(medicalMetadata.rescaleIntercept)
    rescaleSlope = Number(medicalMetadata.rescaleSlope)
  }

  return rawPixelValue * rescaleSlope + rescaleIntercept
}

/**
 * Scale back to raw pixel values, which we remove when encoding in RG channels.
 *
 * @param windowLevelRange The unscaled pixel value in the DICOM png.
 * @param medicalMetadata The medical metadata on the view, if present.
 *
 * @returns The raw value present in the original DICOM.
 */
const rawToPixel = (
  rawPixelValue: number,
  numPixelChannels: number,
  medicalMetadata?: MedicalMetadata,
): number => {
  if (!medicalMetadata) {
    return rawPixelValue
  }

  const smallestImagePixelValue = Number(medicalMetadata.smallestImagePixelValue)
  const largestImagePixelValue = Number(medicalMetadata.largestImagePixelValue)

  const pixelRange = largestImagePixelValue - smallestImagePixelValue

  const pixelValue = ((rawPixelValue - smallestImagePixelValue) * numPixelChannels) / pixelRange

  return pixelValue
}

/**
 * Converts backend window levels from Hounsfield units back to PNG pixel values.
 * This is the inverse of pngPixelValueToScaledPixelValue.
 *
 * Backend window levels are already in Hounsfield units, but the rendering pipeline
 * expects PNG pixel values, so we need to convert them back.
 *
 * @param scaledPixelValue The scaled pixel value (in Hounsfield units)
 * @param numPixelChannels The number of pixel channels
 * @param medicalMetadata The medical metadata for the item
 * @returns The PNG pixel value
 */
export const scaledPixelValueToPngPixelValue = (
  scaledPixelValue: number,
  numPixelChannels: number,
  medicalMetadata?: MedicalMetadata,
): number => {
  if (!medicalMetadata) {
    return scaledPixelValue
  }

  // Reverse of rawToScaled: raw = (scaled - intercept) / slope
  const rescaleIntercept = Number(medicalMetadata.rescaleIntercept || 0)
  const rescaleSlope = Number(medicalMetadata.rescaleSlope || 1)
  const rawPixelValue = (scaledPixelValue - rescaleIntercept) / rescaleSlope

  // Reverse of pixelToRaw: pixel = ((raw - offset) * numChannels) / range
  return rawToPixel(rawPixelValue, numPixelChannels, medicalMetadata)
}

/**
 * Scales an individual DICOM image pixel value to the scaled units dictated by the
 * modality and DICOM imagePixelModule.
 *
 * Note this is rather niave, and only works for simple scalings like MR and CT.
 * It will not work out of the box for PT.
 *
 * @param pixelValue The pixel value to scale
 * @param medicalMetadata The medical metadata on the view, if present.
 * @returns The scaled pixel value
 */
export const pngPixelValueToScaledPixelValue = (
  pixelValue: number,
  numPixelChannels: number,
  medicalMetadata?: MedicalMetadata,
): number => {
  const rawPixelValue = pixelToRaw(pixelValue, numPixelChannels, medicalMetadata)
  const scaledPixelValue = rawToScaled(rawPixelValue, medicalMetadata)

  return scaledPixelValue
}

/**
 * Scales the window level range using the available metadata on the item.
 *
 * @param windowLevelRange The unscaled window level range
 * @param medicalMetadata The medical metadata for the item.
 *
 * @returns The scaled window level range.
 */
export const scaleWindowLevelRange = (
  windowLevelRange: WindowLevels,
  numPixelChannels: number,
  medicalMetadata?: MedicalMetadata,
): WindowLevels => [
  pngPixelValueToScaledPixelValue(windowLevelRange[0], numPixelChannels, medicalMetadata),
  pngPixelValueToScaledPixelValue(windowLevelRange[1], numPixelChannels, medicalMetadata),
]

/**
 * Scales the window level range using the available metadata on the item.
 *
 * @param windowLevelRange The unscaled window level range
 * @param medicalMetadata The medical metadata for the item.
 *
 * @returns The scaled window level range.
 */
export const unscaleWindowLevelRange = (
  scaledWindowLevelRange: WindowLevels,
  numPixelChannels: number,
  medicalMetadata?: MedicalMetadata,
): WindowLevels => [
  scaledPixelValueToPngPixelValue(scaledWindowLevelRange[0], numPixelChannels, medicalMetadata),
  scaledPixelValueToPngPixelValue(scaledWindowLevelRange[1], numPixelChannels, medicalMetadata),
]

/**
 * Converts backend window level range from Hounsfield units to PNG pixel values.
 * This is used when backend window levels need to be fed into the rendering pipeline.
 *
 * @param windowLevelRange The backend window level range in Hounsfield units
 * @param numPixelChannels The number of pixel channels
 * @param medicalMetadata The medical metadata for the item
 * @returns The window level range in PNG pixel values
 */
export const backendWindowLevelRangeToPixelRange = (
  windowLevelRange: WindowLevels,
  numPixelChannels: number,
  medicalMetadata?: MedicalMetadata,
): WindowLevels => [
  scaledPixelValueToPngPixelValue(windowLevelRange[0], numPixelChannels, medicalMetadata),
  scaledPixelValueToPngPixelValue(windowLevelRange[1], numPixelChannels, medicalMetadata),
]
